import { useState } from "react";

import Button from "../../Button";
import Divider from "../../Divider/Divider";
import Dropdown from "../../Dropdown/Dropdown";
import styles from "./ServicesDropdown.module.css";

import { useParams } from "react-router";
import { Module, ModuleCategory } from "../../../constants/modules";
import { titles } from "../../../constants/titles";
import { GroupedModules, useCurrentProductionArea } from "../../../hooks";
import ExternalLinkButton from "../../ExternalLinkButton";
import ServiceLink from "./ServiceLink";

type ServicesDropdownProps = {
  modules: GroupedModules | undefined;
};

function ServicesDropdown({ modules }: ServicesDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { site } = useParams();
  const { productionArea } = useCurrentProductionArea();

  const onOpenSupport = () => {
    setIsOpen(false);
    if (!site) return;
    window.location.assign(`/support-line/site/${encodeURIComponent(site)}`);
  };

  return (
    <div>
      <Dropdown
        anchor="right"
        dropdown={
          modules && (
            <DropdownContent
              baseServices={modules[ModuleCategory.Base]}
              premiumServices={modules[ModuleCategory.Premium]}
              onOpenSupport={onOpenSupport}
            />
          )
        }
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      >
        {productionArea && (
          <Button
            toggled={isOpen}
            isToggle={true}
            variant="transparent"
            size="small"
            onClick={() => setIsOpen(!isOpen)}
            disabled={modules === undefined}
          >
            {titles[productionArea]} Connect Services
          </Button>
        )}
      </Dropdown>
    </div>
  );
}

type DropdownContentProps = {
  baseServices: Module[];
  premiumServices: Module[];
  onOpenSupport: () => void;
};

function DropdownContent({
  baseServices,
  premiumServices,
}: DropdownContentProps) {
  const { productionArea } = useCurrentProductionArea();

  const basePackageEnabled = baseServices.some((s) => s.enabled);
  const performacePackageEnabled = premiumServices.some((s) => s.enabled);

  const areSomeServicesDisabled =
    baseServices.some((s) => !s.enabled) ||
    premiumServices.some((s) => !s.enabled);

  return (
    <div className={styles["services-dropdown"]}>
      <div className={styles.header}>
        <h4>Connect Services</h4>
        {productionArea && (
          <ExternalLinkButton productionArea={productionArea} />
        )}
      </div>
      <div className={styles.body}>
        {baseServices.length > 0 && (
          <>
            {premiumServices.length > 0 && (
              <Divider
                text="Base Package"
                color={
                  basePackageEnabled
                    ? "var(--foreground-secondary-color)"
                    : "var(--foreground-disabled-color)"
                }
                iconName="Star"
              />
            )}
            <div className={styles["service-links-container"]}>
              {baseServices.map((service) => (
                <ServiceLink key={service.id} module={service} />
              ))}
            </div>
          </>
        )}
        {premiumServices.length > 0 && (
          <>
            <Divider
              text="Performance Services"
              color={
                performacePackageEnabled
                  ? "var(--foreground-secondary-color)"
                  : "var(--foreground-disabled-color)"
              }
              iconName="Star"
            />
            <div className={styles["service-links-container"]}>
              {premiumServices.map((service) => (
                <ServiceLink key={service.id} module={service} />
              ))}
            </div>
          </>
        )}
      </div>
      {areSomeServicesDisabled && (
        <div className={styles.footer}>
          <p>Would you like to expand your Service package?</p>
          <p>Contact your ABB representative.</p>
        </div>
      )}
    </div>
  );
}

export default ServicesDropdown;
